/**
 * The webhook console — what arrived from the gateway, and what it caused.
 *
 * **An invalid signature is the view that matters.** Those events are stored precisely so
 * somebody can look at them: each one is either an attacker probing the endpoint or our own
 * secret misconfigured, and both need a person. They are never acted on, however many times the
 * button is pressed.
 *
 * **"What did it do" is a count of real events, not a status.** The inbound record is immutable
 * by design, so the outcomes are read from the payment events that point back at it. A status
 * column would be a field somebody edits during an incident — exactly when the record needs to
 * be the one thing nobody has touched.
 *
 * **The payload is redacted server-side.** Razorpay includes the payer's email and phone; they
 * never reach this screen, because a console half the company can open is not where a devotee's
 * number belongs.
 *
 * **Replay is safe by construction.** Every handler is idempotent — a replayed capture finds the
 * booking already confirmed and says so — which is why this screen offers it without a
 * type-to-confirm ritual that would imply otherwise.
 */
import { useState } from 'react';
import { Button, StatusChip } from '../components/primitives';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { FilterBar, ScopeAction, Section, StatTile } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useReprocessWebhook,
  useResolveWebhook,
  useWebhookDetail,
  useWebhooks,
  type WebhookRow,
} from '../lib/api/hooks/webhooks';
import { useUrlSearch, useUrlTab } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';

const TABS = [
  { key: 'all', label: 'All' },
  // The queue somebody is actually working. Without it the list only ever grows and "nothing
  // needs attention" is a state this console can never reach.
  { key: 'unresolved', label: 'Needs attention' },
  { key: 'invalid', label: 'Bad signature' },
  { key: 'unhandled', label: 'No handler' },
];

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same queue. */
const TAB_KEYS = TABS.map((t) => t.key);

const PAGE_SIZE = 25;

/** The query the chosen tab means. Two of them are different fields, not two values of one. */
export function tabQuery(tab: string): {
  signature?: string;
  handled?: string;
  resolved?: string;
} {
  if (tab === 'invalid') return { signature: 'invalid' };
  if (tab === 'unhandled') return { handled: 'unhandled' };
  if (tab === 'unresolved') return { resolved: 'unresolved' };
  return {};
}

/**
 * What a row's state is, in one word.
 *
 * "Ignored" is deliberately distinct from "did nothing": an unsigned event was refused, an
 * unhandled one was stored for a handler that does not exist yet, and a handled one that caused
 * nothing found no order of ours. Three different things a person would act on differently.
 */
export function rowState(row: WebhookRow): { label: string; tone: 'success' | 'error' | 'warn' | 'neutral' } {
  // Checked first: a resolved event is one somebody has already looked at, whatever it was.
  // Showing it as "refused" alongside the ones still needing attention is how a queue stops
  // being a queue.
  if (row.is_resolved) return { label: 'dealt with', tone: 'neutral' };
  if (!row.signature_valid) return { label: 'refused — bad signature', tone: 'error' };
  if (!row.is_handled) return { label: 'stored, no handler yet', tone: 'neutral' };
  if (row.outcome_count === 0) return { label: 'matched nothing of ours', tone: 'warn' };
  return { label: `${row.outcome_count} recorded`, tone: 'success' };
}

export function Webhooks() {
  const { scope } = useConsole();
  const [tab, setTab] = useUrlTab('webhooks', TAB_KEYS, 'all');
  const [q, setQ] = useUrlSearch('webhooks');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);

  const webhooks = useWebhooks({
    ...tabQuery(tab),
    q: q || undefined,
    page,
    page_size: PAGE_SIZE,
  });
  const detail = useWebhookDetail(openId);
  const reprocess = useReprocessWebhook();
  const resolve = useResolveWebhook();

  const rows = webhooks.data?.items ?? [];
  const total = webhooks.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function narrow(next: () => void) {
    setPage(1);
    next();
  }

  if (openId !== null) {
    return (
      <DetailPage
        title={detail.data?.row.event_name ?? 'Webhook'}
        code={detail.data?.row.provider_event_id ?? undefined}
        status={
          detail.data ? (
            <StatusChip
              tone={rowState(detail.data.row).tone}
              label={rowState(detail.data.row).label}
            />
          ) : undefined
        }
        onClose={() => setOpenId(null)}
        audit="The inbound record is never modified. What it caused is appended elsewhere."
        actions={
          detail.data?.can_reprocess ? (
            <span style={{ display: 'flex', gap: 'var(--sp-xs)' }}>
              <ScopeAction
                scope={scope}
                need="finance"
                size="sm"
                icon="repeat"
                onClick={() => reprocess.mutate(detail.data!.row.id)}
              >
                Replay
              </ScopeAction>
              {!detail.data.row.is_resolved && (
                <ScopeAction
                  scope={scope}
                  need="finance"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    resolve.mutate({
                      eventId: detail.data!.row.id,
                      reason: 'Reviewed from the webhook console and dealt with.',
                    })
                  }
                >
                  Mark dealt with
                </ScopeAction>
              )}
            </span>
          ) : detail.data && !detail.data.row.is_resolved ? (
            // An unsigned event can never be replayed — but somebody still has to be able to
            // say they have looked at it, or the queue never empties.
            <ScopeAction
              scope={scope}
              need="finance"
              size="sm"
              variant="secondary"
              onClick={() =>
                resolve.mutate({
                  eventId: detail.data!.row.id,
                  reason: 'Reviewed from the webhook console and dealt with.',
                })
              }
            >
              Mark dealt with
            </ScopeAction>
          ) : undefined
        }
      >
        {detail.isPending && <LoadingState message="Reading the event…" />}
        {detail.isError && (
          <ErrorState title="Could not read that event" cause={errorMessage(detail.error)} />
        )}
        {detail.isSuccess && (
          <>
            <KV
              rows={[
                ['Provider', detail.data.row.provider],
                ['Event', detail.data.row.event_name],
                ['Signature', detail.data.row.signature_valid ? 'verified' : 'did not verify'],
                ['Received', formatDateTime(detail.data.row.received_at)],
                ['Handler', detail.data.row.is_handled ? 'yes' : 'none for this event type'],
              ]}
            />

            {!detail.data.can_reprocess && (
              <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                This event's signature never verified. It is kept as evidence and is never acted
                on — replaying it would undo the reason we keep it.
              </p>
            )}

            {reprocess.isError && (
              <ErrorState
                title="That replay was refused"
                cause={errorMessage(reprocess.error)}
                inline
              />
            )}
            {/* A refused "dealt with" left the button looking dead — say so instead. */}
            {resolve.isError && (
              <ErrorState
                title="It is not marked dealt with"
                cause={errorMessage(resolve.error)}
                inline
              />
            )}
            {reprocess.isSuccess && (
              <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                Replayed: {reprocess.data.detail}
              </p>
            )}

            <Section
              title="What it caused"
              note="Read from the payment events that point back at this one"
              pad={false}
            >
              {detail.data.outcomes.length === 0 ? (
                <div
                  style={{
                    padding: 'var(--sp-lg)',
                    color: 'var(--c-ink3)',
                    fontSize: 'var(--fs-admin-cell)',
                  }}
                >
                  Nothing. Either no handler claimed it, or it named an order that is not ours.
                </div>
              ) : (
                <DataTable
                  density="compact"
                  columns={[
                    { key: 'at', label: 'When', mono: true },
                    { key: 'kind', label: 'Event' },
                    { key: 'order', label: 'Order', align: 'right', mono: true },
                    { key: 'detail', label: 'Detail' },
                  ]}
                  rows={detail.data.outcomes.map((o, index) => ({
                    id: `${o.event_type}-${index}`,
                    at: formatDateTime(o.at),
                    kind: o.event_type,
                    order: `#${o.payment_order_id}`,
                    detail: o.detail,
                  }))}
                />
              )}
            </Section>

            <Section title="Payload" note="Redacted server-side — no payer contact details" pad={false}>
              <pre
                style={{
                  margin: 0,
                  padding: 'var(--sp-lg)',
                  overflowX: 'auto',
                  fontSize: 'var(--fs-admin-colhead)',
                  color: 'var(--c-ink2)',
                }}
              >
                {JSON.stringify(detail.data.payload, null, 2)}
              </pre>
            </Section>
          </>
        )}
      </DetailPage>
    );
  }

  return (
    <>
      <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
        Every inbound event, kept exactly as it arrived. Nothing on this screen edits one — what
        an event caused is recorded separately, so the record cannot be tidied up afterwards.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 'var(--sp-md)',
        }}
      >
        <StatTile label="Events" value={webhooks.isSuccess ? total : '—'} note="in this view" />
        <StatTile
          label="Acted on"
          value={webhooks.isSuccess ? rows.filter((r) => r.outcome_count > 0).length : '—'}
          note="on this page"
        />
        <StatTile
          label="Bad signature"
          value={webhooks.isSuccess ? rows.filter((r) => !r.signature_valid).length : '—'}
          note="on this page · never acted on"
          tone={rows.some((r) => !r.signature_valid) ? 'error' : 'default'}
        />
      </div>

      <FilterBar
        q={q}
        onQ={(value) => narrow(() => setQ(value))}
        placeholder="Provider event id — press /"
        tabs={TABS}
        tab={tab}
        onTab={(next) => narrow(() => setTab(next))}
      />

      {webhooks.isPending && <LoadingState message="Reading webhooks…" />}
      {webhooks.isError && (
        <ErrorState title="Could not read webhooks" cause={errorMessage(webhooks.error)} />
      )}
      {webhooks.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="webhook"
          title={tab === 'invalid' ? 'No bad signatures' : 'Nothing here'}
          body={
            tab === 'invalid'
              ? 'Every event that arrived verified against our secret.'
              : 'No inbound event matches this filter.'
          }
          inline
        />
      )}

      {rows.length > 0 && (
        <Section
          title="Inbound events"
          note={`${total} ${total === 1 ? 'event' : 'events'} · page ${page} of ${pages}`}
          pad={false}
        >
          <DataTable
            density="compact"
            onRowClick={(row) => setOpenId(Number(row.id))}
            columns={[
              { key: 'event', label: 'Event' },
              { key: 'provider_id', label: 'Provider id', mono: true },
              { key: 'state', label: 'What happened' },
              { key: 'received', label: 'Received', mono: true },
            ]}
            rows={rows.map((row) => {
              const state = rowState(row);
              return {
                id: row.id,
                event: row.event_name,
                provider_id: row.provider_event_id,
                state: <StatusChip tone={state.tone} label={state.label} />,
                received: formatDateTime(row.received_at),
              };
            })}
          />
        </Section>
      )}

      {rows.length > 0 && pages > 1 && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button variant="secondary" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </>
  );
}
